import { ref } from 'vue';

import { useCapacityCalendarStore } from '@/store/capacityCalendarStore';
import { resolvePreviousWeekPlan } from '@/utils/previousWeek';
import type { PreviousWeekPlan } from '@/utils/previousWeek';

export interface NoPlanMenu {
  isMenuCell: (rowId: string, weekKey: string) => boolean;
  openMenu: (rowId: string, weekKey: string) => void;
  // Guarded: a stray close from a cell that is no longer the active one changes nothing.
  closeMenu: (rowId: string, weekKey: string) => void;
  // Unconditional — for a view-level change that invalidates whatever cell was open.
  closeAny: () => void;
  previousWeekPlan: (rowId: string, weekKey: string) => PreviousWeekPlan | null;
  copyPreviousWeek: (rowId: string, weekKey: string) => Promise<void>;
  addCapacity: (rowId: string, weekKey: string, capacity: number) => Promise<void>;
}

// The one no-plan cell (if any) currently showing its copy-previous-week / type-and-add
// micro-menu, and what its two actions do. Shared by the Capacity Calendar and the scheduling
// board so a plan is created the same way from either page. The cells stay dumb (props in,
// events out); the view owns which cell's menu is open through this.
export function useNoPlanMenu(): NoPlanMenu {
  const store = useCapacityCalendarStore();
  const menuCell = ref<{ rowId: string; weekKey: string } | null>(null);

  function isMenuCell(rowId: string, weekKey: string): boolean {
    return menuCell.value?.rowId === rowId && menuCell.value?.weekKey === weekKey;
  }

  function openMenu(rowId: string, weekKey: string): void {
    menuCell.value = { rowId, weekKey };
  }

  function closeMenu(rowId: string, weekKey: string): void {
    if (isMenuCell(rowId, weekKey)) closeAny();
  }

  function closeAny(): void {
    menuCell.value = null;
  }

  function previousWeekPlan(rowId: string, weekKey: string): PreviousWeekPlan | null {
    return resolvePreviousWeekPlan(rowId, weekKey, store.allocations);
  }

  // The menu closes only once the plan exists: a failed create leaves it open with the typed
  // value, so the error toast and a retry are one screen.
  async function create(rowId: string, weekKey: string, plan: PreviousWeekPlan): Promise<void> {
    const created = await store.createAllocation(rowId, weekKey, plan);
    if (created) {
      store.markRecentCells([`${rowId}:${weekKey}`]);
      closeMenu(rowId, weekKey);
    }
  }

  async function copyPreviousWeek(rowId: string, weekKey: string): Promise<void> {
    const plan = previousWeekPlan(rowId, weekKey);
    if (!plan) return;
    await create(rowId, weekKey, plan);
  }

  async function addCapacity(rowId: string, weekKey: string, capacity: number): Promise<void> {
    await create(rowId, weekKey, { capacity, capacityFactor: null });
  }

  return { isMenuCell, openMenu, closeMenu, closeAny, previousWeekPlan, copyPreviousWeek, addCapacity };
}
